<template>
  <!-- Option Name (ไทย) -->
  <td class="py-2 align-middle">
    <div class="input-group input-group-sm">
      <input
        type="text"
        class="form-control shadow-none"
        v-model.trim="v$.option.name.$model"
        :placeholder="$t('Enter option name') + '...'"
        style="font-size: 13px;"
      >
    </div>
    <div v-for="error of v$.option.name.$errors" class="d-block invalid-feedback" :key="error.$uid">
      {{ error.$message }}
    </div>
  </td>

  <!-- Option Name (อังกฤษ) — optional ไม่กรอกก็ได้ ระบบ fallback ไปใช้ชื่อไทย -->
  <td class="py-2 align-middle">
    <div class="input-group input-group-sm">
      <input
        type="text"
        class="form-control shadow-none"
        v-model.trim="v$.option.name_en.$model"
        :placeholder="$t('Optional') + '...'"
        style="font-size: 13px;"
      >
    </div>
    <div v-for="error of v$.option.name_en.$errors" class="d-block invalid-feedback" :key="error.$uid">
      {{ error.$message }}
    </div>
  </td>

  <!-- Price -->
  <td class="py-2 align-middle">
    <div class="input-group input-group-sm">
      <span
        class="input-group-text border-end-0 text-secondary"
        style="font-size: 12px; background: #f8f9fa;"
      >+</span>
      <input
        type="number"
        class="form-control shadow-none text-end border-start-0"
        v-model.trim="v$.option.price_adjustment.$model"
        placeholder="0.00"
        style="font-size: 13px;"
      >
    </div>
    <div v-for="error of v$.option.price_adjustment.$errors" class="d-block invalid-feedback" :key="error.$uid">
      {{ error.$message }}
    </div>
  </td>

  <!-- Default -->
  <td class="py-2 align-middle">
    <label
      class="d-flex align-items-center gap-2 mb-0"
      :for="'defaultOption'+index"
      style="cursor: pointer;"
    >
      <input
        v-if="$parent.form.allow_multiple"
        class="form-check-input shadow-none m-0 flex-shrink-0"
        v-model="v$.option.is_default.$model"
        type="checkbox"
        :id="'defaultOption'+index"
      >
      <input
        v-else
        class="form-check-input shadow-none m-0 flex-shrink-0"
        v-model="v$.option.is_default.$model"
        @change="() => { this.$emit('set-default', this.index); }"
        type="radio"
        :id="'defaultOption'+index"
        :value="true"
      >
      <span
        class="badge"
        :style="option.is_default
          ? 'background: #d4edda; color: #155724; border: 1px solid #c3e6cb;'
          : 'background: #f8f9fa; color: #6c757d; border: 1px solid #dee2e6;'"
        style="font-size: 11px; font-weight: 500;"
      >
        {{ option.is_default ? $t('Yes') : $t('No') }}
      </span>
    </label>
  </td>

  <!-- Recipe — ซ่อนเมื่อแพ็กไม่มี feature_inventory (สูตรเดิมยังส่งกลับไปครบตอนบันทึก) -->
  <td v-if="$hasFeature('feature_inventory')" class="py-2 align-middle">
    <div class="d-flex flex-wrap gap-1 align-items-center">
      <span
        v-if="option.split_exempt"
        class="badge"
        style="background: #fff7f2; color: #b34700; border: 1px solid #E96C15; font-size: 10px; font-weight: 600;"
      >
        <i class="fa-solid fa-thumbtack me-1" style="font-size: 9px; opacity: 0.7;" />
        {{ $t('Fixed') }}
      </span>
      <span
        v-for="(recipe, i) in option.recipes"
        :key="i"
        class="badge d-inline-flex align-items-center gap-1"
        style="background: #fff3e8; color: #7a3a00; border: 1px solid #eeb084; font-size: 10px; font-weight: 500; max-width: 120px;"
      >
        <i class="fa-solid fa-flask-vial" style="font-size: 9px; opacity: 0.7;" />
        <span class="text-truncate" style="max-width: 90px;">{{ recipe.name }}</span>
      </span>
      <span
        v-if="option.recipes.length === 0"
        class="badge"
        style="background: #f8f9fa; color: #6c757d; border: 1px solid #dee2e6; font-size: 10px; font-weight: 500;"
      >
        <i class="fa-solid fa-ban me-1" style="font-size: 9px; opacity: 0.7;" />
        {{ $t('No recipe') }}
      </span>
    </div>
  </td>

  <!-- Action -->
  <td class="text-end py-2 align-middle">
    <div class="d-inline-flex align-items-center gap-1">
      <popper
        v-if="$hasFeature('feature_inventory')"
        :placement="'top'"
        :hover="true"
        :arrow="true"
        :disableClickAway="true"
        class="popper-default border-0 m-0"
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
          style="font-size: 10px; width: 24px; height: 24px; padding: 0;"
          @click="openModal"
        >
          <i :class="option.recipes.length > 0 ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'" />
        </button>
        <template #content>
          <div style="font-size: 12px;">{{ option.recipes.length > 0 ? $t('Edit Recipe') : $t('Add Recipe') }}</div>
        </template>
      </popper>
      <popper
        :placement="'top'"
        :hover="true"
        :arrow="true"
        :disableClickAway="true"
        class="popper-default border-0 m-0"
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center"
          style="font-size: 10px; width: 24px; height: 24px; padding: 0;"
          @click="$parent.deleteItem(index)"
        >
          <i class="fa-solid fa-trash" />
        </button>
        <template #content>
          <div style="font-size: 12px;">{{ $t('Delete') }}</div>
        </template>
      </popper>
    </div>
  </td>
</template>

<script lang="js">
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-option";
import Popper from "vue3-popper";
import moment from "moment";
import numeral from 'numeral';
import Select2 from 'vue3-select2-component';
import Ladda from '@/plugins/Ladda.vue';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return validation(this.option)
  },
  props: {
    option: {
      type: Object,
      default: []
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    let vm = this;
    return {
    }
  },
  components: {
    Select2,
    Ladda,
    Popper
  },
  methods: {
    openModal() {
      this.$parent.modalRecipeForm.open = true;
      this.$parent.modalRecipeForm.recipes = _.cloneDeep(this.option.recipes);
      this.$parent.modalRecipeForm.optionIndex = this.index;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
  },
  async mounted() {},
  computed: {
  },
  watch: {
  }
}
</script>
